import { createAsyncThunk } from "@reduxjs/toolkit";

import * as basketAPI from "../../../api/basket";
import { IBasket, IUserConfigurableBundle } from "../../../models/catalogue";

/**
 * Parameters for loadUserConfigurableBundles thunk.
 */
interface LoadUserConfigurableBundlesParams {
    basket: IBasket;
}

/**
 * Load user configurable bundles for basket.
 *
 * Fetches the configurable bundles (gifts, add-ons) available for the current
 * basket. These bundles allow users to select and customize gift products
 * during checkout. The bundles are based on promotions and basket contents.
 *
 * @param {LoadUserConfigurableBundlesParams} params - The bundle loading parameters
 * @param {IBasket} params.basket - The current basket to load bundles for
 *
 * @returns {Promise<IUserConfigurableBundle[]>} Promise resolving to array of configurable bundles
 *
 * @example
 * // Load bundles when user reaches gift selection step
 * dispatch(loadUserConfigurableBundles({ basket }));
 *
 * @example
 * // Reload bundles after basket changes
 * const bundles = await dispatch(loadUserConfigurableBundles({ basket })).unwrap();
 * console.log("Available gift bundles:", bundles.length);
 *
 * @sideEffects
 * - Updates `gifts.userConfigurableBundles` with fetched bundles via extraReducers
 * - Updates `gifts.status` to LOADED via extraReducers
 */
export const loadUserConfigurableBundles = createAsyncThunk(
    "checkout/loadUserConfigurableBundles",
    async (
        params: LoadUserConfigurableBundlesParams,
    ): Promise<IUserConfigurableBundle[]> => {
        const { basket } = params;

        // Load configurable bundles for the given basket
        const bundles = await basketAPI.listUserConfigurableBundles(basket);

        return bundles;
    },
);
